// AI models (docs/cockpit/spec.md, Setup; ADR-0017, ADR-0019): which model tier does which kind of work, in plain
// words, what each used of the Model Budget this month, and whether any model call leaves India and under which data
// terms. Read-only: Credits and usage is where the budget is topped up. No provider or model is named.
import { useEffect, useState } from "react";
import { ApiError, type Api } from "../api.js";
import { creditsOf } from "../credits/Credits.js";
import { formatNumber } from "../i18n/format.js";
import { useLanguage, useWords, type Words } from "../i18n/words.js";
import type { ModelsView } from "./api.js";

const problemOf = (error: unknown, w: Words) =>
  error instanceof ApiError
    ? error.problems.length > 0
      ? error.problems.join("; ")
      : w.ownerPagesRefused(error.code)
    : w.ownerPagesUnreachable;

export function Models({ api }: { api: Api }) {
  const w = useWords();
  const { language } = useLanguage();
  const [models, setModels] = useState<ModelsView | undefined>(undefined);
  const [problem, setProblem] = useState<string | undefined>(undefined);

  useEffect(() => {
    let live = true;
    api.models.view().then(
      (view) => live && setModels(view),
      (error: unknown) => live && setProblem(problemOf(error, w)),
    );
    return () => {
      live = false;
    };
  }, [api, w]);

  if (!models) return problem ? <p role="alert">{problem}</p> : <p role="status">{w.modelsLoading}</p>;
  const credits = (milli: number) => creditsOf(milli, language);
  return (
    <>
      <p className="muted">{w.modelsIntro}</p>
      <div className="tbl-wrap">
        <table className="tbl">
          <thead>
            <tr>
              <th scope="col">{w.modelsWork}</th>
              <th scope="col">{w.modelsWhat}</th>
              <th scope="col">{w.modelsUsed}</th>
              <th scope="col">{w.modelsWhere}</th>
            </tr>
          </thead>
          <tbody>
            {models.tiers.map((t) => (
              <tr key={t.tier}>
                <th scope="row">{w.modelsTierName(t.tier)}</th>
                <td>{w.modelsTierWork(t.tier)}</td>
                <td>
                  {t.tier === "T0"
                    ? w.modelsSteps(t.count, formatNumber(t.count, language), credits(t.milliCredits))
                    : w.modelsCalls(t.count, formatNumber(t.count, language), credits(t.milliCredits))}
                </td>
                <td>{t.tier === "T0" ? w.modelsNoModel : t.leavesIndia ? w.modelsOutsideIndia : w.modelsInsideCell}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <h2>{w.modelsBudget}</h2>
      <p>
        {w.modelsBudgetUsed(credits(models.usedMilliCredits), credits(models.budgetMilliCredits), models.usedPercent)}{" "}
        {w.modelsBudgetLeft(credits(models.remainingMilliCredits))}
      </p>
      <h2>{w.modelsData}</h2>
      <p>{models.leavesIndia ? w.modelsLeavesIndia : w.modelsStaysInIndia}</p>
      {models.leavesIndia ? (
        <ul>
          {models.terms.noTraining ? <li>{w.modelsNoTraining}</li> : null}
          {models.terms.zeroRetention ? <li>{w.modelsZeroRetention}</li> : null}
        </ul>
      ) : null}
      <p className="muted">{w.modelsSensitive}</p>
    </>
  );
}
